import type { AttachmentPickers, ReadOnlyRibbonControlDefProps } from "owa-mail-ribbon-utils";
import type { RibbonTabDefinitionBase } from "owa-acui";
import type { RibbonDefinitionResult } from "owa-command-ribbon";
import type { ComposeViewState } from "owa-mail-compose-store/lib/store/schema/ComposeViewState";
import { owaComputedFn } from "owa-computed-fn";
import { getComposeViewState } from "owa-mail-reading-pane-store-conversation/lib/utils/getComposeViewState";
import getSLRHomeTab from "./slr-home-tab/getSLRHomeTab";
import getSLRNotesTab from "./slr-notes-tab/getSLRNotesTab";
import getSLRSendReceiveTab from "./slr-send-receive-tab/getSLRSendReceiveTab";
import getSLRViewTab from "./slr-view-tab/getSLRViewTab";
import getSLRSearchToolsTab from "./slr-search-tab/getSLRSearchToolsTab";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";
import { isNotesScenario } from "owa-mail-ribbon-utils/lib/isNotesScenario";
import { getRibbonMailboxInfo } from "owa-mail-ribbon-utils/lib/getRibbonMailboxInfo";
import { getHelpTab } from "owa-ribbon-help";
import { getSelectedTab } from "owa-mail-ribbon-store/lib/selectors/getSelectedTab";
import type { MailboxInfo } from "owa-client-types";
import { lazyGetSLRComposeTabs } from "../../lazyComposeFunctions";
import getComposePlaceholderTabs from "../../util/getComposePlaceholderTabs";
import getIsSearchTableShown from "owa-mail-list-store/lib/utils/getIsSearchTableShown";
import getSelectedTableView from "owa-mail-list-store/lib/utils/getSelectedTableView";
import { isCopilotResultsTable } from "owa-mail-list-store/lib/store/schema/CopilotResultsTableQuery";
import { type PerformanceDatapoint } from "owa-analytics";
import { type ComposePropsCache, getOrUpdateComposeProps } from "../utils/getOrUpdateComposeProps";

const composePropsCache: ComposePropsCache = {
    composePropsGlobal: undefined,
    prevAttachmentPickers: undefined,
    prevViewState: undefined,
};

let stableResult: RibbonDefinitionResult | null = null;

// Clarification: "readOnly" does not mean that this property never has its properties changed.
// "readOnly" in this context means the mail state, so "not compose".
// Importantly, mailboxInfo can change and we track that change.
let readOnlyPropsGlobal: ReadOnlyRibbonControlDefProps = {
    isPopout: false,
    mailboxInfo: undefined,
    itemId: undefined,
    projectionTabId: undefined,
    selectedRowKey: undefined,
};

function buildSLRComposeResult(viewState: ComposeViewState, attachmentPickers: AttachmentPickers): {
    tabs: RibbonTabDefinitionBase[];
    scenarioDatapoint?: PerformanceDatapoint;
} {
    const composeProps = getOrUpdateComposeProps(composePropsCache, viewState, attachmentPickers);
    
    /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
     * BASELINE. Do not copy and paste!
     *	> 'tryImportForRender' is restricted from being used. tryImportForRender should only be used in rare circumstances and can hurt TTI if used incorrectly. */
    const getSLRComposeTabsFn = lazyGetSLRComposeTabs.tryImportForRender();
    const tabs = getSLRComposeTabsFn
        ? getSLRComposeTabsFn(composeProps)
        : getComposePlaceholderTabs(viewState.editorId, false /* isMlr */, viewState.mailboxInfo);
    
    return {
        tabs,
        scenarioDatapoint: viewState.perfDatapoint,
    };
}

export const getMailRibbonDefinitionsSLR: (attachmentPickers: AttachmentPickers) => RibbonDefinitionResult = owaComputedFn(function getMailRibbonDefinitionsSLR(attachmentPickers: AttachmentPickers): RibbonDefinitionResult {
    const tabs: RibbonTabDefinitionBase[] = [];
    
    // Check if we need to update readOnlyProps for Mailbox Info
    const mailboxInfo: MailboxInfo = getRibbonMailboxInfo(undefined /* RibbonControlDefinitionProps */);
    
    const viewState: ComposeViewState | undefined = getComposeViewState();
    /* When compose has started opening but hasn't rendered yet, return the last stable
       result (same object reference). Because owaComputedFn/computedFn uses reference
       equality, returning the same reference suppresses propagation to CommandRibbon,
       eliminating the intermediate re-render caused by Home/View tab computeds changing
    */
    if (isGlobalFeatureEnabled("cmp-ribbon-compose-opt") &&
        viewState !== undefined &&
        !viewState.isComposeRendered &&
        stableResult !== null) {
        return stableResult;
    }
    
    if (mailboxInfo !== readOnlyPropsGlobal.mailboxInfo) {
        readOnlyPropsGlobal = {
            ...readOnlyPropsGlobal,
            mailboxInfo,
        };
    }
    
    tabs.push(getSLRHomeTab(readOnlyPropsGlobal));
    
    if (isWin32StylingEnabled()) {
        const sendReceiveTab = getSLRSendReceiveTab(readOnlyPropsGlobal);
        if (sendReceiveTab) {
            tabs.push(sendReceiveTab);
        }
    }
    
    tabs.push(getSLRViewTab(readOnlyPropsGlobal), getHelpTab(getSelectedTab()));
    
    if (viewState?.isComposeRendered) {
        const composeResult = buildSLRComposeResult(viewState, attachmentPickers);
        tabs.push(...composeResult.tabs);
    }
    
    if (isNotesScenario()) {
        tabs.push(getSLRNotesTab(readOnlyPropsGlobal));
    }
    
    // Add Search Tools tab when in search mode or copilot results table
    if ((getIsSearchTableShown() || isCopilotResultsTable(getSelectedTableView()?.tableQuery)) &&
        isGlobalFeatureEnabled("sea-searchRibbon")) {
        const tableView = getSelectedTableView();
        const scenarioType = (tableView?.tableQuery as any)?.scenarioType;
        if (scenarioType !== "messageSuggestion" && scenarioType !== "queryOnType") {
            tabs.push(getSLRSearchToolsTab(readOnlyPropsGlobal));
        }
    }
    
    const result: RibbonDefinitionResult = {
        tabs,
        scenarioDatapoint: viewState?.perfDatapoint,
    };
    
    if (isGlobalFeatureEnabled("cmp-ribbon-compose-opt")) {
        // Update the stable cache whenever compose is NOT in a launching state (no viewState at all or compose fully launched)
        const isComposeNotLaunching = !viewState || viewState.isComposeRendered;
        if (isComposeNotLaunching) {
            stableResult = result;
        }
    }
    
    return result;
});
